import { compact, filter, partition, sortBy, sumBy } from 'lodash';
import { toM3 } from '../../constants/VolumeUnits';
import type {
  ScenarioTimeline_CargoCompartmentFragment,
  ScenarioTimeline_ShipFragment,
} from '../../graphql';
import { CargoType, HoldType, VolumeUnits } from '../../graphql';
import { floatGt } from '../../lib/math';
import type { ScenarioOrder } from '../../lib/planningData';
import type { DefinedFields } from '../../lib/util';
import { assertedDefined, exhaustiveCheck, mapKeyBy } from '../../lib/util';
import { generateClientId } from './ClientId';
import type { MassVolumeSize } from './Capacity';
import type {
  LoadEvent,
  TCTimelineEvent,
  TCOrderEvent,
  Events,
} from './TimeData';
import {
  isDischargeEvent,
  isLoadEvent,
  isTCOrderEvent,
  isCargoEvent,
} from './TimeData';
import type { CargoAllocation } from '@seaber/common/core/entries';
import {
  DefaultMap,
  getSumBy,
  mapGroupBy,
} from '@seaber/common/lib/collections';
import type { CargoAllocationOptions } from './suggestions/Suggestions';

export type CargoCompartment = ScenarioTimeline_CargoCompartmentFragment;

export function getCompartments(
  vessel: ScenarioTimeline_ShipFragment,
): ScenarioTimeline_CargoCompartmentFragment[] {
  const byShip = compact(vessel?.cargoCompartmentsByShipId?.nodes);
  if (byShip.length > 0) return byShip;
  const byVesselInfo = compact(
    vessel?.vesselInfoByVesselInfoId?.cargoCompartmentsByVesselId?.nodes,
  );
  return byVesselInfo;
}

/** Can the given allocation type be assigned to the compartment  */
export function isCorrectCargoAllocationType(
  cargoType: CargoType | null,
  compartment: Pick<CargoCompartment, 'type'>,
): boolean {
  if (!cargoType) return true;
  switch (cargoType) {
    case CargoType.GAS:
      return compartment.type === HoldType.GAS;
    case CargoType.LIQUID:
      return compartment.type === HoldType.LIQUID;
    case CargoType.SOLID:
      return compartment.type === HoldType.HOLD;
    default:
      exhaustiveCheck(cargoType);
  }
}

type ActuallyLoadedAllocation = {
  compartmentId: number;
  orderId: number;
  assortmentId: number;
  amountM3: number;
};

export type LoadedCompartment = {
  compartment: CargoCompartment;
  allocations: Readonly<ActuallyLoadedAllocation>[];
  assortment: ScenarioOrder['assortmentByAssortmentId'];
};

let warnedNoCargoType = false;

/** Returns the total amount that fit to each compartment based on assigned allocations.
 * Mutates the spaceLeftByCompartment map with added amounts. */
export function getAndAddLoadedAmountToCompartments(
  assortment: ScenarioOrder['assortmentByAssortmentId'],
  newAllocations: TCOrderEvent['cargoAllocations'],
  compartmentById: Map<number, LoadedCompartment>,
): number {
  if (!assortment) {
    if (newAllocations?.length) {
      console.error(
        `No assortment defined while computing allocated volume for order ${newAllocations[0].orderId}`,
      );
    }
    return 0;
  }
  const cargoType = assortment.cargoType;
  if (!cargoType) {
    if (newAllocations?.length) {
      if (!warnedNoCargoType) {
        warnedNoCargoType = true;
        console.warn(`No cargo type defined while computing allocated volume`);
      }
    }
  }
  let totalLoaded = 0;
  for (const ca of newAllocations) {
    const compartment = compartmentById.get(ca.compartmentId);
    if (!compartment) {
      console.warn(
        `Compartment ${ca.compartmentId} not found while computing allocated volume for order ${newAllocations[0].orderId}`,
      );
      continue;
    }
    const isCorrectHoldType = isCorrectCargoAllocationType(
      cargoType ?? null,
      compartment.compartment,
    );
    if (!isCorrectHoldType) continue;

    const spaceLeft =
      +compartment.compartment.maxVolume -
      sumBy(compartment.allocations, 'amountM3');
    const canAssortmentBeAllocated =
      !compartment.assortment || compartment.assortment.id === assortment.id;
    const allocatedM3 = canAssortmentBeAllocated ? +ca.amount : 0;
    const actuallyLoaded = Math.min(spaceLeft, allocatedM3);
    if (actuallyLoaded > 0) {
      compartment.assortment = assortment;
    }
    compartment.allocations.push({
      compartmentId: compartment.compartment.id,
      orderId: ca.orderId,
      assortmentId: assortment.id,
      amountM3: actuallyLoaded,
    });
    totalLoaded += actuallyLoaded;
  }
  return totalLoaded;
}

/** Removes cargo from allocation state when discharging
 * Mutates the spaceLeftByCompartment map with removed amounts. */
export function releaseOrderFromCompartments(
  orderId: number,
  compartmentById: Map<number, LoadedCompartment>,
): void {
  for (const [, compartment] of compartmentById) {
    if (compartment.allocations.find((a) => a.orderId === orderId)) {
      compartment.allocations = compartment.allocations.filter(
        (a) => a.orderId !== orderId,
      );
      if (compartment.allocations.length === 0) {
        compartment.assortment = null;
      }
    }
  }
}

type OrderWithAssortment = DefinedFields<
  ScenarioOrder,
  'assortmentByAssortmentId'
>;

export function orderHasAssortment(
  order: ScenarioOrder,
): order is OrderWithAssortment {
  return !!order.assortmentByAssortmentId;
}

function getCompartmentVolumeM3(compartment: CargoCompartment) {
  return toM3(
    +compartment.maxVolume,
    compartment.volumeUnit ?? VolumeUnits.M_3,
  );
}

/** Collects maximum space needed for each order's assortment together with other orders of the same kind.
 * Used to advice tank size selection in cargo allocation.
 * For example, if we carry at the same time orders 1, 2, 3 and assortments A and B as:
 * 1: A,100, 2: A,150, 3: B,200, then result will be { 1: 250, 2: 250, 3: 200 } (which advices that orders 1&2 together should use the larger tank)*/
export function getMaxLoadedAmountsByOrder(
  events: TCTimelineEvent[],
): Map<number, number> {
  const currentLoadedAmountsByAssortment = new Map<number, number>();
  /** Assortment id by order id, for the currently carried orders only  */
  const currentCarriedOrdersAssortments = new Map<number, number>();

  return events.reduce((result, event) => {
    if (isCargoEvent(event)) {
      const { order } = event;
      const assortmentId = assertedDefined(
        order.assortmentByAssortmentId?.id,
        `Order ${order.id} has no assortment`,
      );
      const amount = order.cargoRanges.volume.max;
      const oldAmount = currentLoadedAmountsByAssortment.get(assortmentId) ?? 0;
      const newAmount = isLoadEvent(event)
        ? oldAmount + amount
        : oldAmount - amount;
      currentLoadedAmountsByAssortment.set(assortmentId, newAmount);

      if (isLoadEvent(event)) {
        currentCarriedOrdersAssortments.set(order.id, assortmentId);
        for (const [
          carriedOrderId,
          carriedOrderAssortment,
        ] of currentCarriedOrdersAssortments) {
          if (carriedOrderAssortment !== assortmentId) continue;
          const existingMax = result.get(carriedOrderId) ?? 0;
          if (newAmount > existingMax) {
            result.set(carriedOrderId, newAmount);
          }
        }
      } else {
        currentCarriedOrdersAssortments.delete(order.id);
      }
    }
    return result;
  }, new Map<number, number>());
}

/** Returns compartments where it's legal to allocate given assortment to, paired with respective space left in ascending order */
export function getPossibleCompartmentsWithSpace(
  assortment: NonNullable<ScenarioOrder['assortmentByAssortmentId']>,
  cargoCompartments: CargoCompartment[],
  currentAllocationsByCompartment: Map<
    number,
    (CargoAllocation | NewCargoAllocation)[]
  >,
  existingOverlappingAllocations: Map<
    number,
    (NewCargoAllocation | CargoAllocation)[]
  >[],
  orderById: Map<number, TCOrderEvent['order']>,
  orderId: number,
): [compartment: CargoCompartment, spaceLeft: number][] {
  const cargoType = assortment.cargoType;
  const possibleCompartments = cargoCompartments.filter(
    (c) =>
      isCorrectCargoAllocationType(cargoType ?? null, c) &&
      !currentAllocationsByCompartment
        .get(c.id)
        ?.some(
          (a) =>
            orderById.get(a.orderId)?.assortmentByAssortmentId?.id !==
            assortment.id,
        ) &&
      !existingOverlappingAllocations.some((allocationsByCompartment) =>
        allocationsByCompartment
          .get(c.id)
          ?.some(
            (a) =>
              orderById.get(a.orderId)?.assortmentByAssortmentId?.id !==
              assortment.id,
          ),
      ),
  );

  const getMaxExistingCompartmentAllocation = (compartmentId: number) => {
    return existingOverlappingAllocations.reduce(
      (result, allocationsByCompartment) => {
        const allocations = allocationsByCompartment
          .get(compartmentId)
          ?.filter((a) => a.orderId !== orderId);
        if (!allocations) return result;
        const maxForOrder = sumBy(allocations, (a) => +a.amount);
        return Math.max(result, maxForOrder);
      },
      0,
    );
  };

  const withSpace: [CargoCompartment, number][] = possibleCompartments
    .map((c): [CargoCompartment, number] => [
      c,
      getCompartmentVolumeM3(c) -
        sumBy(
          currentAllocationsByCompartment.get(c.id) ?? [],
          (a) => +a.amount,
        ) -
        getMaxExistingCompartmentAllocation(c.id),
    ])
    .filter(([, spaceLeft]) => floatGt(spaceLeft, 0));

  const sortedBySpace = sortBy(withSpace, ([, spaceLeft]) => spaceLeft);

  return sortedBySpace;
}

export type NewCargoAllocation = Omit<
  CargoAllocation,
  'scheduledOrderClientId'
>;

/** Collect which orders are carried simultaneously and loaded after the current one.
 * Used to calculate how much of the current cargo we are allowed to load within its margins. */
export function getOverlappingLoadsByOrder(
  events: readonly Events[],
): Map<number, LoadEvent[]> {
  const currentCarriedOrders = new Set<number>();
  const result = new DefaultMap<number, LoadEvent[]>(() => []);

  for (const e of events) {
    if (isLoadEvent(e)) {
      for (const orderId of currentCarriedOrders) {
        result.get(orderId).push(e);
      }
      currentCarriedOrders.add(e.order.id);
    } else if (isDischargeEvent(e)) {
      currentCarriedOrders.delete(e.order.id);
    }
  }

  return result;
}

/** Returns a ratio for how much of the current order can be loaded in order to leave enough room for later simultaneously carried orders (using min margin on those).
 * Always at least the minimum amount, even if it doesn't allow rest of the orders to fit.
 * The ratio is relative to the max margin of the current order. */
export function getMaxAllowedLoadAmount(
  { order: { cargoRanges } }: LoadEvent,
  availableCapacity: MassVolumeSize,
  overlappingLoads: LoadEvent[],
): number {
  if (
    cargoRanges.mass.max === 0 ||
    cargoRanges.volume.max === 0 ||
    overlappingLoads.length === 0
  ) {
    return 1;
  }
  const mimimumMassCapacityRequiredAfter = getSumBy(
    overlappingLoads,
    (e) => e.order.cargoRanges.mass.min,
  );
  const mimimumVolumeCapacityRequiredAfter = getSumBy(
    overlappingLoads,
    (e) => e.order.cargoRanges.volume.min,
  );

  const maxAllowedMass = Math.max(
    0,
    availableCapacity.mass - mimimumMassCapacityRequiredAfter,
  );
  const maxAllowedVolume = Math.max(
    0,
    availableCapacity.volume - mimimumVolumeCapacityRequiredAfter,
  );

  const maxAllowedAmountByMass = Math.min(
    maxAllowedMass / cargoRanges.mass.max,
    1,
  );
  const maxAllowedAmountByVolume = Math.min(
    maxAllowedVolume / cargoRanges.volume.max,
    1,
  );

  const minimumMassRatio = cargoRanges.mass.min / cargoRanges.mass.max;
  const minimumVolumeRatio = cargoRanges.volume.min / cargoRanges.volume.max;

  return Math.max(
    Math.min(maxAllowedAmountByMass, maxAllowedAmountByVolume),
    minimumMassRatio,
    minimumVolumeRatio,
  );
}

/** For each order we have all existing cargo allocations which overlap with it at different stages.
 * We use his information to calculate the maximum amount of cargo that can still fit compartments,
 * accounting for any cargo that is already loaded before or will be loaded after. */
export function getOverlappingAllocationsByOrder(
  events: TCOrderEvent[],
): Map<number, Map<number, (NewCargoAllocation | CargoAllocation)[]>[]> {
  const currentAllocationsByCompartment = new Map<
    number,
    (NewCargoAllocation | CargoAllocation)[]
  >();
  const currentCarriedOrders = new Set<number>();
  const result = new DefaultMap<
    number,
    Map<number, (NewCargoAllocation | CargoAllocation)[]>[]
  >(() => []);

  /** We collect maximally allocated combinations so for example we don't need to add a combination loading orders [1,2]
   * if we also load the order 3 next (resulting in [1,2,3]), because we only care about the most restrictive existing allocations. */
  let wasLoadedMore = false;

  for (const e of events) {
    if (isLoadEvent(e)) {
      wasLoadedMore = true;
      currentCarriedOrders.add(e.order.id);
      for (const ca of e.cargoAllocations) {
        const existingAllocations =
          currentAllocationsByCompartment.get(ca.compartmentId) ?? [];
        currentAllocationsByCompartment.set(ca.compartmentId, [
          ...existingAllocations,
          ca,
        ]);
      }
    } else {
      if (wasLoadedMore) {
        wasLoadedMore = false;
        const currentResult = new Map(currentAllocationsByCompartment);
        for (const orderId of currentCarriedOrders) {
          result.get(orderId).push(currentResult);
        }
      }
      currentCarriedOrders.delete(e.order.id);
      for (const ca of e.cargoAllocations) {
        const existingAllocations =
          currentAllocationsByCompartment.get(ca.compartmentId) ?? [];
        currentAllocationsByCompartment.set(
          ca.compartmentId,
          existingAllocations.filter((a) => a.orderId !== ca.orderId),
        );
      }
    }
  }

  return result;
}

export function createNewCargoAllocations(
  fullSchedule: TCTimelineEvent[],
  orderIdsToAllocate: number[],
  combineSameAssortment: boolean,
): NewCargoAllocation[] {
  /** Only cargo events, other events don't affect tank allocations */
  const schedule = filter(fullSchedule, isTCOrderEvent);
  const vessel = schedule.at(0)?.vessel;
  if (!vessel) {
    console.error(`Attempted to allocate cargo without any loaded orders`);
    return [];
  }
  const cargoCompartments = getCompartments(vessel);
  const allOrdersById = mapKeyBy(
    filter(schedule, isLoadEvent).map((e) => e.order),
    (o) => o.id,
  );
  const maxLoadedAmountsByOrder = getMaxLoadedAmountsByOrder(schedule);
  const existingOverlappingAllocationsByOrder =
    getOverlappingAllocationsByOrder(schedule);

  const currentAllocationsByCompartment = new DefaultMap<
    number,
    (CargoAllocation | NewCargoAllocation)[]
  >(() => []);
  const currentNewAllocationsByCompartment = new DefaultMap<
    number,
    (CargoAllocation | NewCargoAllocation)[]
  >(() => []);

  /** Returns new allocations. Updates currentAllocationsByCompartment. */
  function allocateOneOrder(
    order: OrderWithAssortment,
    prevAssortmentAmount: number,
  ): NewCargoAllocation[] {
    const result: NewCargoAllocation[] = [];

    /** How much left to allocate of the total maximum size carried together with the current order at any time. */
    let assortmentSizeLeft = combineSameAssortment
      ? (maxLoadedAmountsByOrder.get(order.id) ?? 0) - prevAssortmentAmount
      : order.cargoRanges.volume.max;

    let orderSizeLeft = order.cargoRanges.volume.max;
    while (floatGt(orderSizeLeft, 0)) {
      const availableCompartments = getPossibleCompartmentsWithSpace(
        order.assortmentByAssortmentId,
        cargoCompartments,
        currentNewAllocationsByCompartment,
        existingOverlappingAllocationsByOrder.get(order.id) ?? [],
        allOrdersById,
        order.id,
      );
      const [empty, nonEmpty] = partition(
        availableCompartments,
        ([compartment]) =>
          currentAllocationsByCompartment.get(compartment.id).length === 0,
      );

      const compartmentWithSpace =
        (combineSameAssortment &&
          // Try to find a compartment that has enough space left for the whole assortment and already contains the same assortment
          (nonEmpty.find(([, spaceLeft]) => assortmentSizeLeft <= spaceLeft) ??
            // Try to find a compartment that has enough space left for this order and contains the same assortment
            nonEmpty.find(([, spaceLeft]) => orderSizeLeft <= spaceLeft) ??
            // Doesn't fit fully to any, fill the largest one
            nonEmpty.at(-1))) ||
        // Try to find an empty compartment that has enough space for the whole order and other orders of the same assortment
        (empty.find(([, spaceLeft]) => assortmentSizeLeft <= spaceLeft) ??
          // Doesn't fit fully to any, fill the largest one
          empty.at(-1));

      if (!compartmentWithSpace) break;

      const [compartment, spaceLeft] = compartmentWithSpace;
      if (!floatGt(spaceLeft, 0)) {
        break;
      }
      const allocated = Math.min(spaceLeft, orderSizeLeft);

      const newAllocation: NewCargoAllocation = {
        compartmentId: compartment.id,
        amount: allocated,
        orderId: order.id,
        clientId: generateClientId(),
      };

      orderSizeLeft -= allocated;
      assortmentSizeLeft -= allocated;
      currentAllocationsByCompartment.get(compartment.id).push(newAllocation);
      currentNewAllocationsByCompartment
        .get(compartment.id)
        .push(newAllocation);
      result.push(newAllocation);
    }
    return result;
  }

  /** Collects all allocations along all events for result */
  const newAllocationsByOrderId = new DefaultMap<number, NewCargoAllocation[]>(
    () => [],
  );
  /** Changes during iteration */
  const currentAllocationsByOrderId = new DefaultMap<
    number,
    NewCargoAllocation[]
  >(() => []);
  const currentAmountsByAssortment = new DefaultMap<number, number>(() => 0);

  // Hold current compartment allocation state through whole schedule, create new allocations for orders that need it
  for (const event of schedule) {
    if (!(isCargoEvent(event) && orderHasAssortment(event.order))) continue;

    const { order } = event;
    const { assortmentByAssortmentId: assortment } = order;
    const prevAssortmentAmount =
      currentAmountsByAssortment.get(assortment.id) ?? 0;

    let orderAllocations: readonly (NewCargoAllocation | CargoAllocation)[];
    if (isLoadEvent(event)) {
      if (orderIdsToAllocate.includes(order.id)) {
        // Skip if the order is already fully allocated
        const existingAllocations = event.cargoAllocations;
        const totalAllocated = sumBy(existingAllocations, (a) => +a.amount);
        if (
          floatGt(totalAllocated, 0) &&
          totalAllocated >= order.cargoRanges.volume.max
        ) {
          orderAllocations = existingAllocations;
        } else {
          orderAllocations = allocateOneOrder(order, prevAssortmentAmount);
          newAllocationsByOrderId.get(order.id).push(...orderAllocations);
        }
      } else {
        orderAllocations = event.cargoAllocations;
        for (const orderAllocation of orderAllocations) {
          currentAllocationsByCompartment
            .get(orderAllocation.compartmentId)
            .push(orderAllocation);
        }
      }

      currentAllocationsByOrderId.get(order.id).push(...orderAllocations);
    } else {
      orderAllocations = currentAllocationsByOrderId.get(order.id);

      for (const orderAllocation of orderAllocations) {
        currentAllocationsByCompartment.update(
          orderAllocation.compartmentId,
          (compartmentAllocations) => {
            return (compartmentAllocations ?? []).filter(
              (a) => a.orderId !== orderAllocation.orderId,
            );
          },
        );
        currentNewAllocationsByCompartment.update(
          orderAllocation.compartmentId,
          (compartmentAllocations) => {
            return (compartmentAllocations ?? []).filter(
              (a) => a.orderId !== orderAllocation.orderId,
            );
          },
        );
      }
      currentAllocationsByOrderId.delete(order.id);
    }
    const orderAllocatedVolume = sumBy(orderAllocations, (a) =>
      isLoadEvent(event) ? +a.amount : -a.amount,
    );
    const currentAssortmentAmount = prevAssortmentAmount + orderAllocatedVolume;
    currentAmountsByAssortment.set(assortment.id, currentAssortmentAmount);
  }

  return [...newAllocationsByOrderId.values()].flat();
}

/** Generates new cargo allocations and applies them to the schedule. Returns a new array. */
export function applyNewCargoAllocations(
  schedule: TCTimelineEvent[],
  orderIdsToAllocate: number[],
  allocationOptions: CargoAllocationOptions,
) {
  const newCargoAllocations = createNewCargoAllocations(
    schedule,
    orderIdsToAllocate,
    allocationOptions.combineSameAssortment,
  );
  const newCargoAllocationsByOrderId = mapGroupBy(
    newCargoAllocations,
    (a) => a.orderId,
  );
  schedule = schedule.map((event) => {
    if (isCargoEvent(event)) {
      const cargoAllocations =
        newCargoAllocationsByOrderId.get(event.order.id) ?? [];
      return !orderIdsToAllocate.includes(event.order.id) &&
        cargoAllocations.length === 0
        ? event
        : {
            ...event,
            cargoAllocations: event.cargoAllocations.concat(
              cargoAllocations ?? [],
            ),
            isNew: true,
          };
    } else {
      return event;
    }
  });
  return schedule;
}
